import { useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { PencilSimple, CloudArrowUp, X } from "@phosphor-icons/react";
import { useContent } from "@/context/ContentContext";

export const EditToolbar = () => {
  const { isAdminEditing, drafts, publishAll, discardAll, exitEditMode } = useContent();
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  if (!isAdminEditing || location.pathname.startsWith("/admin")) return null;
  const n = Object.keys(drafts).length;

  const publish = async () => {
    if (!n) {
      toast.info("No changes yet — click any highlighted text to edit it first.");
      return;
    }
    setBusy(true);
    try {
      await publishAll();
      toast.success("Published! Your changes are now live on the website.");
    } catch (e) {
      toast.error("Publish failed. Please try again.");
    }
    setBusy(false);
  };

  const discard = () => {
    if (n && !window.confirm("Discard all unpublished edits?")) return;
    discardAll();
  };

  const exit = () => {
    if (n && !window.confirm("You have unpublished edits. Exit anyway and lose them?")) return;
    exitEditMode();
  };

  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] w-[calc(100%-2rem)] sm:w-auto" data-testid="edit-toolbar">
      <div className="glass-panel border border-gold/40 rounded-full px-5 py-3 flex flex-wrap items-center justify-center gap-3 sm:gap-4 shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
        <span className="flex items-center gap-2 text-[11px] text-[#e6dff5]">
          <PencilSimple size={15} weight="duotone" className="text-gold" />
          <span className="hidden sm:inline">Click any highlighted text or photo to edit it</span>
          <span className="sm:hidden">Tap text or photo to edit</span>
        </span>
        <span
          className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border rounded-full ${n ? "border-prosper/60 text-prosper" : "border-white/20 text-muted-foreground"}`}
          data-testid="edit-pending-count"
        >
          {n} change{n === 1 ? "" : "s"}
        </span>
        <button
          onClick={publish}
          disabled={busy}
          data-testid="edit-publish-button"
          className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-gold text-black text-[10px] uppercase tracking-widest font-bold hover:bg-[#e8c95a] disabled:opacity-50 transition-colors duration-300"
        >
          <CloudArrowUp size={14} weight="bold" /> {busy ? "Publishing..." : "Publish"}
        </button>
        <button onClick={discard} data-testid="edit-discard-button" className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-red-400 transition-colors duration-300">
          Discard
        </button>
        <button onClick={exit} data-testid="edit-exit-button" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-gold transition-colors duration-300">
          <X size={13} /> Exit
        </button>
      </div>
    </div>
  );
};
